/**
 * The drop engine — every place a block can go, and why it cannot go elsewhere.
 *
 * Task 0031, headless half. No DOM type appears in this file: the split is the
 * same one `edit.ts` / `edit-host.ts` already make, for the same reason. There
 * is more than one conformant renderer, and a permission boundary duplicated
 * per renderer is a permission boundary that disagrees with itself.
 *
 * Joaquín, on what this is for:
 *
 * > but most importantly, adaptive layout — like when you grab a block in
 * > notion and all different sections get highlighted where you can put them
 * > and how
 *
 * The thing that makes the highlight worth anything is that it is drawn from
 * **legality**, not from geometry. A wall of uniform green teaches the user
 * that the highlight means nothing, which is why 0030's child grammar had to
 * exist before this could.
 *
 * ## Three decisions that are load-bearing, and were nearly made wrong
 *
 * **Enumeration is total over the spec, and unbounded by viewport.** Measured
 * at 0.57 ms at the node cap. I argued for viewport-bounding it early and was
 * wrong twice over: the canon does not do it, and `IRNode.virtualize` marks
 * collections over sixty items rather than visibility, so it could not have
 * bounded anything anyway.
 *
 * **`consequence` is arithmetic, never a speculative compile.**
 * `presentationOf` decides on four integers — media count, field count,
 * `groupBy`, `flow` — so the effect of a hypothetical drop is four integers of
 * arithmetic. `compileSpec` measures 5.19 ms; five hundred of those is 2.6
 * seconds of dead air at drag start. The 5.2 ms I once blamed on enumeration
 * was this, and I told Codex the opposite before I measured it.
 *
 * **`wrap` and `replace` are not enumerated.** They are six of every seven
 * candidates, and a twenty-block screen blows any ranking budget before ranking
 * runs. They are generated for the resolved target once the pointer has settled,
 * which is the only moment they can be described to the user anyway.
 */

import {
  admits, slotsOf, countValues, MAX_VALUES, validateSpec,
  DOCK_EDGES, ATTACHMENTS,
  type NodeKind, type TLayoutSpec, type TNode, type DockEdge, type Attachment,
} from '@morph/spec';
import type { IR, IRNode } from './ir.js';

/**
 * Where a dropped block lands relative to the node it was dropped on.
 *
 * `beside` is Notion's COLUMNS, and it is the only one that changes the shape
 * of the tree rather than an index in it. `before` and `after` put a block
 * above or below its neighbour in whatever flow the parent already has;
 * `beside` puts the two in a ROW — wrapping them in a new region when the
 * parent is a stack, and inserting as an ordinary sibling when it is already a
 * row, because a third column beside two is not a fresh pair.
 *
 * The renderer has drawn `flow: 'row'` for a long time —
 * `.m-region.m-row{flex-direction:row}`. What did not exist was any way for a
 * person to ask for one: every drop position offered was a position in a
 * stack, so a lens could be built with columns and never edited into them.
 */
export const DROP_RELATIONS = ['before', 'after', 'beside', 'into', 'place'] as const;
export type DropRelation = (typeof DROP_RELATIONS)[number];

export type Placement =
  | { readonly mode: 'docked'; readonly edge: DockEdge }
  | { readonly mode: 'floating'; readonly rest: Attachment };

/** What is being dragged. A move and an insert differ only in whether the node
 *  is already in the spec — the legality question is identical. */
export interface Payload {
  readonly kind: NodeKind;
  readonly node: TNode;
  /** Present when this is a MOVE: the IR index of the node being moved. */
  readonly from?: number;
}

export type Presentation = 'board' | 'gallery' | 'table' | 'list' | 'cards';

export interface DropTarget {
  readonly relation: DropRelation;
  /** IR index of the node this drop is described against. */
  readonly host: number;
  readonly slot?: 'children' | 'item' | 'slot';
  /** Position within the slot, for `before` / `after` / `into`. */
  readonly index?: number;
  /**
   * `beside` only: the IR index of the sibling the dropped node pairs with.
   *
   * `index` cannot carry it. After the wrap there is ONE child where there were
   * two, so the applier needs the node it is pairing with rather than the gap
   * beside it — a position that stops existing the moment the drop is applied.
   */
  readonly pairWith?: number;
  readonly placement?: Placement;
  readonly ok: boolean;
  /** Why not. A drop engine that only says no is a wall the user cannot read. */
  readonly why?: string;
  /**
   * Nodes that would stop being rendered if this drop happened.
   *
   * A cards-to-table flip renders columns only, so a `node-slot` panel in the
   * item template disappears from the screen — and "cards → table" does not say
   * so anywhere. A drop that silently deletes something the user can see is the
   * one an undo will be reached for, so it is named before it happens.
   */
  readonly destroys?: readonly number[];
  /** What the host collection's presentation becomes. Arithmetic, not a compile. */
  readonly consequence?: Presentation;
}

export interface Acceptance {
  readonly ok: boolean;
  readonly why?: string;
  readonly destroys?: readonly number[];
}

/* --------------------------------------------------------------- legality */

const KINDS_IN_SUBTREE = (ir: IR, root: number): IRNode[] => {
  const out: IRNode[] = [];
  const stack = [root];
  while (stack.length) {
    const n = ir.nodes[stack.pop()!];
    if (!n) continue;
    out.push(n);
    stack.push(...n.children);
  }
  return out;
};

/** Is `i` inside a collection's item template? Walks up, which is O(depth). */
function insideItemTemplate(ir: IR, i: number): boolean {
  let at = ir.nodes[i];
  while (at && at.parent >= 0) {
    const parent = ir.nodes[at.parent];
    if (parent?.kind === 'collection') return true;
    at = parent;
  }
  return false;
}

/**
 * May this payload go into this host's slot?
 *
 * Table lookups and arithmetic, in that order, and the arithmetic counts in
 * **values** rather than spec nodes — 0030's currency. A card with sixteen
 * bindings is one node and thirty-four values, so a budget in nodes is a budget
 * in the wrong unit and would let a drop through that the validator then
 * refuses. A highlight the commit gate rejects is worse than no highlight.
 */
export function canAccept(
  ir: IR, host: number, slot: 'children' | 'item' | 'slot', payload: Payload,
  opts: { readonly totalValues?: number; readonly index?: number } = {},
): Acceptance {
  const h = ir.nodes[host];
  if (!h) return { ok: false, why: 'no such node' };

  if (!admits(h.kind as NodeKind, slot, payload.kind)) {
    return { ok: false, why: `a ${h.kind} does not admit a ${payload.kind} here` };
  }

  const grammar = slotsOf(h.kind as NodeKind).find((s) => s.slot === slot);
  if (grammar && h.children.length >= grammar.max) {
    return { ok: false, why: `this ${h.kind} already holds its maximum of ${grammar.max}` };
  }

  // 0030's containment rule, which the grammar table cannot express because it
  // is about the subtree rather than the slot.
  if (payload.kind === 'collection' && (h.kind === 'collection' || insideItemTemplate(ir, host))) {
    return {
      ok: false,
      why: 'a collection inside an item template renders the whole object set once per object',
    };
  }

  // A polymorphic collection tries its item specs IN ORDER, so every one but
  // the last needs a predicate saying when it applies — the validator's
  // `polymorphic-without-predicate`. The dropped node carries no `when`, so it
  // can only be the last, and it can only be added at all if the items already
  // there have one.
  //
  // Found by the equivalence test: `canAccept` said yes, the commit gate said
  // no, and a highlight the gate rejects is exactly the thing that test exists
  // to catch.
  if (slot === 'item') {
    const templates = h.itemTemplates ?? [];
    const index = opts.index ?? templates.length;
    if (index < templates.length) {
      return { ok: false, why: 'an item spec with no predicate has to be the last one' };
    }
    const undecided = templates.filter((t) => t.when === undefined).length;
    if (undecided > 0) {
      return {
        ok: false,
        why: 'the item specs already here need a "when" predicate before another can follow',
      };
    }
  }

  // A node cannot be dropped inside itself. Moving a region into its own child
  // detaches the tree from the root and nothing after it renders.
  if (payload.from !== undefined) {
    let at: IRNode | undefined = ir.nodes[host];
    while (at) {
      if (at.i === payload.from) return { ok: false, why: 'a block cannot be moved inside itself' };
      at = at.parent >= 0 ? ir.nodes[at.parent] : undefined;
    }
  }

  const added = countValues(payload.node, MAX_VALUES);
  const total = (opts.totalValues ?? 0) + added;
  if (total > MAX_VALUES) {
    return { ok: false, why: `this would take the layout past its ${MAX_VALUES}-value budget` };
  }

  return { ok: true, ...destroyedBy(ir, host, payload) };
}

/**
 * What a drop would stop rendering.
 *
 * Only a collection can do this, and only by changing which presentation branch
 * the renderer takes: `table` renders columns — field nodes — and nothing else,
 * so media, node-slots and capability modules in the item template vanish.
 */
function destroyedBy(ir: IR, host: number, payload: Payload): { destroys?: readonly number[] } {
  const coll = collectionFor(ir, host);
  if (!coll) return {};
  const before = presentationArith(ir, coll, 0, 0);
  const after = presentationArith(
    ir, coll,
    payload.kind === 'media' ? 1 : 0,
    payload.kind === 'field' ? 1 : 0,
  );
  if (before === after || after !== 'table') return {};

  const tpl = coll.itemTemplates?.[0];
  if (!tpl) return {};
  const lost = KINDS_IN_SUBTREE(ir, tpl.root)
    .filter((n) => n.kind === 'media' || n.kind === 'node-slot' || n.kind === 'capability')
    .map((n) => n.i);
  return lost.length ? { destroys: lost } : {};
}

/** The collection whose presentation this position affects, if any. */
function collectionFor(ir: IR, i: number): IRNode | undefined {
  let at = ir.nodes[i];
  while (at) {
    if (at.kind === 'collection') return at;
    at = at.parent >= 0 ? ir.nodes[at.parent] : undefined;
  }
  return undefined;
}

/**
 * `presentationOf`, as arithmetic over a hypothetical.
 *
 * Deliberately a separate implementation from the renderer's, and deliberately
 * kept beside a test that pins the two together over every combination. The
 * alternative is compiling a speculative spec per candidate, which is 5.19 ms
 * each and the reason drag would feel dead.
 */
export function presentationArith(
  ir: IR, collection: IRNode, extraMedia: number, extraFields: number,
  opts: { readonly single?: boolean } = {},
): Presentation {
  const tpl = collection.itemTemplates?.[0];
  if (!tpl) return 'cards';
  const kids = KINDS_IN_SUBTREE(ir, tpl.root);
  const media = kids.filter((n) => n.kind === 'media').length + extraMedia;
  const fields = kids.filter((n) => n.kind === 'field').length + extraFields;

  if (media > 0) return collection.flow === 'grid' ? 'gallery' : 'list';
  if (collection.query?.groupBy) return 'board';
  if (fields >= 2 && !(opts.single ?? false)) return 'table';
  return 'cards';
}

/* ------------------------------------------------------------ enumeration */

/**
 * Every position, with its verdict.
 *
 * Total over the spec. `before` and `after` for each node that has a parent,
 * `into` for each slot of each host, and `place` for each edge of the root
 * surface — without which "drag the panel to the bottom" is not in the
 * candidate set at any ranking, which is 0028's whole point arriving here.
 *
 * Refusals are RETURNED, not filtered. The overlay wants to draw them, and the
 * user wants to know why a place is not offered — "you cannot put a collection
 * in a row" is a better answer than a region that simply never lights up.
 */
export function enumerateDrops(ir: IR, payload: Payload): DropTarget[] {
  /**
   * A payload that cannot be DETACHED gets no offers — task 0164. The root
   * surface (no parent) and any node behind a slot `pathTo` cannot yet
   * address have no path for `move()` to splice from, so every target
   * painted for them was a promise `applyDrop` refused at commit with a
   * validator code — the overlay's own honesty contract inverted. An
   * INSERTION (`from` unset) never detaches and is untouched.
   */
  if (payload.from !== undefined) {
    const path = pathTo(ir, payload.from);
    // A numeric last segment is what `move()` can splice from. The second
    // member of this class was found by the exhaustive move-agreement test
    // minutes after the first: a collection's item TEMPLATE lives at
    // `items[N].spec` — a non-empty path ending in a key, not an index — so
    // every offer for it died at commit with "the moved node is not in a
    // list".
    if (!path || path.length === 0
      || typeof path[path.length - 1] !== 'number') return [];
  }
  const out: DropTarget[] = [];
  const totalValues = countValues(ir.nodes.length, MAX_VALUES);

  for (const n of ir.nodes) {
    // `into` — one candidate per slot this kind exposes.
    for (const slot of slotsOf(n.kind as NodeKind)) {
      const index = slot.slot === 'item'
        ? (n.itemTemplates?.length ?? 0) : n.children.length;
      const verdict = canAccept(ir, n.i, slot.slot, payload, { totalValues, index });
      out.push({
        relation: 'into', host: n.i, slot: slot.slot, index,
        ...verdict, ...consequenceOf(ir, n.i, payload),
      });
    }

    // `before` / `after` — a sibling position, judged against the PARENT, which
    // is the node that will actually hold it.
    if (n.parent < 0) continue;
    const parent = ir.nodes[n.parent];
    if (!parent) continue;
    const slot = slotsOf(parent.kind as NodeKind)[0];
    if (!slot) continue;
    const at = parent.children.indexOf(n.i);
    for (const [relation, index] of [['before', at], ['after', at + 1]] as const) {
      const verdict = canAccept(ir, parent.i, slot.slot, payload, { totalValues, index });
      out.push({
        relation, host: parent.i, slot: slot.slot, index,
        ...verdict, ...consequenceOf(ir, parent.i, payload),
      });
    }

    // `beside` — the two become a ROW. Offered against the parent like the two
    // above, and only where the parent holds CHILDREN: an `items` slot is a
    // collection's card template, and two templates side by side is not a
    // column, it is two ways to draw one object.
    // A REGION parent, and nothing else. Only a region has both a `children`
    // array to wrap into and a `flow` to make a row — a collection's `items`
    // are card templates, and two templates side by side is not a column but
    // two ways to draw one object. An exhaustive agreement test caught the
    // looser version immediately: `canAccept` said yes where the applier had
    // nothing to pair with, which is the "fast check and real gate disagree"
    // failure that file exists to refuse.
    if (slot.slot === 'children' && parent.kind === 'region') {
      // A BESIDE target pairs with a sibling rather than inserting into a host.
      // Judge that identity here: canAccept only sees the parent and therefore
      // cannot distinguish "beside myself" from a legal sibling pair.
      const verdict = payload.from === n.i
        ? { ok: false as const, why: 'a block cannot be placed beside itself' }
        : canAccept(ir, parent.i, slot.slot, payload, { totalValues, index: at + 1 });
      out.push({
        relation: 'beside', host: parent.i, slot: slot.slot, index: at + 1,
        // WHICH sibling to pair with. `index` alone cannot say: after the wrap
        // there is one child where there were two, so the applier needs the
        // node, not the gap next to it.
        pairWith: n.i,
        ...verdict, ...consequenceOf(ir, parent.i, payload),
      });
    }
  }

  // `place` — 0028's algebra, as drop candidates. Only against the root, which
  // is the surface a panel is placed within.
  const root = ir.nodes[ir.root];
  if (root && root.kind === 'region' && payload.kind !== 'field'
      && payload.kind !== 'media' && payload.kind !== 'node-slot') {
    for (const edge of DOCK_EDGES) {
      out.push({ relation: 'place', host: ir.root, placement: { mode: 'docked', edge }, ok: true });
    }
    for (const rest of ATTACHMENTS) {
      out.push({ relation: 'place', host: ir.root, placement: { mode: 'floating', rest }, ok: true });
    }
  }

  return out;
}

function consequenceOf(ir: IR, host: number, payload: Payload): { consequence?: Presentation } {
  const coll = collectionFor(ir, host);
  if (!coll) return {};
  return {
    consequence: presentationArith(
      ir, coll,
      payload.kind === 'media' ? 1 : 0,
      payload.kind === 'field' ? 1 : 0,
    ),
  };
}

/* ---------------------------------------------------------------- commit */

/** Named `DropRefusal` rather than `Refusal`: `edit.ts` already exports one with
 *  a closed `RefusalCode`, and two different shapes under one name in the same
 *  barrel is how a caller ends up checking the wrong thing. */
export interface DropRefusal {
  readonly ok: false; readonly code: string; readonly message: string;
}
export type DropOutcome<T> = { readonly ok: true; readonly value: T } | DropRefusal;

const no = (code: string, message: string): DropRefusal => ({ ok: false, code, message });

/**
 * The authority a structural edit needs.
 *
 * A viewer holding two field grants must not be able to restructure a layout.
 * Decision 0015 (F21) says the intended form is an action with declared
 * effects, which task 0029 now provides — so this is that, rather than a
 * bespoke permission: the caller declares `edit-layout` or it is refused.
 */
export interface StructuralAuthority {
  readonly effects: readonly string[];
}

/**
 * Apply a drop, and refuse rather than produce a spec nobody validated.
 *
 * The gate is `validateSpec` **then** `compileSpec`, in that order, because the
 * compiler assumes a validated tree and the validator is the thing that can say
 * why. Both run on the result rather than on a prediction: `canAccept` exists to
 * make the highlight fast and honest, not to replace the check.
 */
export function applyDrop(
  spec: TLayoutSpec, ir: IR, target: DropTarget, payload: Payload,
  authority: StructuralAuthority,
): DropOutcome<TLayoutSpec> {
  if (!authority?.effects?.includes('edit-layout')) {
    return no('no-structural-authority',
      'restructuring a layout needs an authority declaring "edit-layout"');
  }
  if (!target.ok) return no('illegal-drop', target.why ?? 'this drop is not legal');

  const path = pathTo(ir, target.host);
  if (!path) return no('unknown-host', 'the drop target is not a node in this spec');

  let next: TLayoutSpec;
  try {
    next = payload.from === undefined
      ? insert(spec, path, target, payload)
      : move(spec, ir, target, payload);
  } catch (e) {
    // A crash is not a refusal — the rule `pool.ts` states twice, applied to the
    // one function here that builds a new tree.
    return no('malformed-drop', e instanceof Error ? e.message : String(e));
  }

  const check = validateSpec(next);
  if (!check.valid) {
    return no('refused-by-validator', check.problems.map((p) => p.code).join(', '));
  }
  return { ok: true, value: next };
}

/**
 * The spec subtree an IR node came from.
 *
 * A MOVE has to carry the real node. The first version of the overlay built a
 * stub — `{ kind, id }` — and every drop was refused by the validator with a
 * wall of `shape` problems, because a card with no bindings and a region with no
 * children are not specs. Obvious in hindsight; invisible until a real drag ran
 * in a browser.
 */
export function specNodeAt(spec: TLayoutSpec, ir: IR, i: number): TNode | undefined {
  const path = pathTo(ir, i);
  if (!path) return undefined;
  let at: unknown = spec.root;
  for (const key of path) {
    if (at === null || typeof at !== 'object') return undefined;
    at = (at as Record<string | number, unknown>)[key as never];
  }
  return (at ?? undefined) as TNode | undefined;
}

/** Property path from the spec root to an IR node, as spec keys. */
function pathTo(ir: IR, i: number): (string | number)[] | undefined {
  const node = ir.nodes[i];
  if (!node) return undefined;
  const rev: (string | number)[] = [];
  let at = node;
  while (at.parent >= 0) {
    const parent = ir.nodes[at.parent];
    if (!parent) return undefined;
    const at_ = parent.children.indexOf(at.i);
    if (at_ < 0) return undefined;
    switch (parent.kind) {
      case 'region': rev.push(at_, 'children'); break;
      case 'card': rev.push(at_, 'children'); break;
      case 'collection': rev.push('spec', at_, 'items'); break;
      default: return undefined;   // capability slots need the slot name; not yet
    }
    at = parent;
  }
  return rev.reverse();
}

/**
 * A move: take the node out of where it was, then put it where it goes.
 *
 * Insert alone DUPLICATED the block — dragging a panel left the original in
 * place and added a second one, which a browser drag showed immediately and no
 * unit test would have.
 *
 * This one copies the whole root rather than two paths, and that is a
 * deliberate exception to the rule below. Path-copying is what makes
 * ENUMERATION affordable, and enumeration runs per candidate per pointer move;
 * a commit runs once, on pointerup. Removing a node invalidates every path
 * computed before it, so two path-copies would have to be re-derived against a
 * tree that no longer matches the IR — which is the kind of index arithmetic
 * that is wrong before it is slow. Measured at well under a millisecond for a
 * real spec, against the ~3 ms the commit gate is allowed.
 */
function move(spec: TLayoutSpec, ir: IR, target: DropTarget, payload: Payload): TLayoutSpec {
  const root = JSON.parse(JSON.stringify(spec.root)) as Record<string, unknown>;
  const paired = target.pairWith === undefined
    ? undefined : specNodeAt(spec, ir, target.pairWith);
  const pairId = (paired as { id?: unknown } | undefined)?.id;

  const from = pathTo(ir, payload.from!);
  if (!from || from.length === 0) throw new Error('the moved node has no path');

  // Detach first, holding on to the node itself.
  const parentPath = from.slice(0, -1);
  const key = from[from.length - 1]!;
  let holder: unknown = root;
  for (const k of parentPath) holder = (holder as Record<string, unknown>)[k as never];
  if (!Array.isArray(holder)) throw new Error('the moved node is not in a list');
  const [detached] = holder.splice(key as number, 1);
  if (detached === undefined) throw new Error('nothing at that position');

  // `items` holds `{ spec }` wrappers; `children` holds nodes.
  const node = (detached as { spec?: unknown }).spec ?? detached;

  // Re-derive the destination against the tree as it is NOW. The IR still
  // describes the tree as it was, so a path taken from it can be stale by one
  // index — which is the whole reason this function does not path-copy.
  const to = pathTo(ir, target.host);
  if (!to) throw new Error('the drop target has no path');
  const shifted = adjust(to, from);

  let host: unknown = root;
  for (const k of shifted) host = (host as Record<string, unknown>)[k as never];
  if (host === null || typeof host !== 'object') throw new Error('the drop target vanished');

  // A MOVE detaches before it inserts. The target index was enumerated against
  // the old children array, so it is stale whenever the moving block appeared
  // before the sibling it pairs with. Re-find that sibling by its validated,
  // spec-wide unique id in the post-detach host.
  let destination = target;
  if (target.relation === 'beside') {
    const kids = Array.isArray((host as Record<string, unknown>)['children'])
      ? (host as Record<string, unknown>)['children'] as unknown[] : [];
    const at = kids.findIndex((kid) =>
      kid !== null && typeof kid === 'object'
      && (kid as Record<string, unknown>)['id'] === pairId);
    if (at < 0) throw new Error('the block to put this beside vanished');
    destination = { ...target, index: at + 1 };
  }

  const placed = destination.relation === 'place'
    ? { ...(node as object), placement: destination.placement }
    : node;

  /**
   * THE SAME `place` THE INSERT PATH USES. **Task 0089.**
   *
   * This used to open-code the three cases below it — `place`, `item`, and a
   * generic `children` splice — and so it had no `beside` branch at all. A
   * `beside` fell into the generic splice and became an ordinary insert: the
   * node moved, no row was created, and `applyDrop` returned `ok`.
   *
   * **Columns could therefore never be made by dragging.** `move` is the path
   * every drag of an existing block takes — `payload.from` is always set — and
   * `place`, which holds the wrap, was only ever reached from `insert`, the
   * path for a node that is not on screen yet.
   *
   * `columns.test.ts` passes `{ kind: 'card', node: card('new') }` with no
   * `from`, so every column test in the suite exercised `insert`. The unit
   * tests and the product were never on the same path, which is the exact
   * *"fast check and real gate disagree"* failure this file's own comments warn
   * about twice — and which an exhaustive agreement test caught once already
   * for `canAccept`. Nothing had pinned `insert` and `move` together.
   *
   * Measured before the change, on the demo's live spec: six accepting `beside`
   * targets, six applications, `ok: true` every time, and not one `flow: 'row'`
   * in any result.
   *
   * Written back rather than mutated, because `place` returns a NEW host — the
   * insert path is built out of copies and this one out of a deep clone, so the
   * result has to be assigned into the parent rather than relied on to alias.
   */
  const rebuilt = place(host, destination, placed as TNode, idsIn(node, idsIn(root)));
  if (shifted.length === 0) {
    return { ...spec, root: rebuilt as TNode };
  }
  let owner: unknown = root;
  for (const k of shifted.slice(0, -1)) owner = (owner as Record<string, unknown>)[k as never];
  (owner as Record<string | number, unknown>)[shifted[shifted.length - 1] as never] = rebuilt;

  return { ...spec, root: root as unknown as TNode };
}

/**
 * A destination path, corrected for a removal that already happened.
 *
 * If the removed node was an earlier sibling on the destination's own path,
 * every index after it shifted down by one. Only the ONE segment where the two
 * paths diverge can be affected: above the divergence they are the same nodes,
 * and below it the removal is in a different subtree entirely.
 */
function adjust(to: (string | number)[], from: (string | number)[]): (string | number)[] {
  const out = [...to];
  for (let i = 0; i < Math.min(out.length, from.length); i += 1) {
    if (out[i] === from[i]) continue;
    if (typeof out[i] === 'number' && typeof from[i] === 'number'
        && i === from.length - 1 && (out[i] as number) > (from[i] as number)) {
      out[i] = (out[i] as number) - 1;
    }
    break;
  }
  return out;
}

/** Copy along the path only — never `structuredClone`, which is the whole spec
 *  per candidate and was measured as the reason the commit did not fit a frame. */
function insert(
  spec: TLayoutSpec, path: (string | number)[], target: DropTarget, payload: Payload,
): TLayoutSpec {
  const node = target.relation === 'place'
    ? { ...(payload.node as object), placement: target.placement } as TNode
    : payload.node;

  const clone = (value: unknown, at: number): unknown => {
    if (at >= path.length) return place(value, target, node, idsIn(node, idsIn(spec.root)));
    const key = path[at]!;
    if (Array.isArray(value)) {
      const copy = value.slice();
      copy[key as number] = clone(copy[key as number], at + 1);
      return copy;
    }
    const copy = { ...(value as Record<string, unknown>) };
    copy[key as string] = clone(copy[key as string], at + 1);
    return copy;
  };

  return { ...spec, root: clone(spec.root, 0) as TNode };
}

function idsIn(value: unknown, out = new Set<string>()): Set<string> {
  if (value === null || typeof value !== 'object') return out;
  if (Array.isArray(value)) {
    for (const item of value) idsIn(item, out);
    return out;
  }
  const record = value as Record<string, unknown>;
  if (typeof record['id'] === 'string') out.add(record['id']);
  for (const child of Object.values(record)) idsIn(child, out);
  return out;
}

function withoutPlacement<T>(value: T): T {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return value;
  const { placement: _placement, ...inline } = value as Record<string, unknown>;
  return inline as unknown as T;
}

function freshRowId(sibling: unknown, used: ReadonlySet<string>): string {
  const raw = sibling !== null && typeof sibling === 'object'
    ? (sibling as Record<string, unknown>)['id'] : undefined;
  const stem = typeof raw === 'string' && raw ? raw : 'column';
  const base = 'row-' + stem;
  if (!used.has(base)) return base;
  let suffix = 2;
  while (used.has(base + '-' + suffix)) suffix += 1;
  return base + '-' + suffix;
}

function place(
  host: unknown, target: DropTarget, node: TNode,
  usedIds: ReadonlySet<string> = new Set(),
): unknown {
  const h = { ...(host as Record<string, unknown>) };
  if (target.relation === 'beside') {
    // NOTION'S COLUMNS. The dropped node and the one it was dropped beside
    // become a row.
    //
    // Already a row → an ordinary insert. A third column beside two is not a
    // fresh pair, and wrapping again would nest rows inside rows until the
    // depth guard refused the spec.
    const kids = Array.isArray(h['children']) ? [...(h['children'] as unknown[])] : [];
    if (h['flow'] === 'row') {
      kids.splice(target.index ?? kids.length, 0, withoutPlacement(node));
      h['children'] = kids;
      return h;
    }
    // A stack → wrap the pair. The new region takes an id derived from the
    // sibling's, because `validate.ts` refuses `duplicate-id` and a fixed name
    // would collide the second time somebody makes a column.
    const at = (target.index ?? 1) - 1;
    const sibling = kids[at];
    if (sibling === undefined) throw new Error('nothing to put this beside');
    kids[at] = {
      kind: 'region',
      id: freshRowId(sibling, usedIds),
      flow: 'row',
      // Placement belongs to a surface child. Once either block becomes a
      // column child it is inline; carrying dock/floating metadata into the
      // row would make the renderer treat the child as a second surface.
      children: [withoutPlacement(sibling), withoutPlacement(node)],
    };
    h['children'] = kids;
    return h;
  }
  if (target.relation === 'place') {
    // A placed node joins the surface's children; the placement is what moves it.
    const kids = Array.isArray(h['children']) ? [...(h['children'] as unknown[])] : [];
    kids.push(node);
    h['children'] = kids;
    return h;
  }
  const index = target.index ?? 0;
  if (target.slot === 'item') {
    const items = Array.isArray(h['items']) ? [...(h['items'] as unknown[])] : [];
    items.splice(index, 0, { spec: node });
    h['items'] = items;
    return h;
  }
  const kids = Array.isArray(h['children']) ? [...(h['children'] as unknown[])] : [];
  kids.splice(index, 0, node);
  h['children'] = kids;
  return h;
}
